import React, { useState } from 'react';
import { Phone, Mail, MapPin, Heart, X } from 'lucide-react';
import { siteConfig, legalLinks, legalContent } from '../data/mock';
import { trackPhoneClick } from '../lib/analytics';

const LegalModal = ({ isOpen, onClose, content }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-xl font-semibold text-gray-800">{content?.title}</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <p className="text-gray-600 whitespace-pre-line leading-relaxed">
            {content?.content}
          </p>
        </div>
      </div>
    </div>
  );
};

const Footer = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState(null);

  const openLegalModal = (type) => {
    const contentMap = {
      '#aviso-legal': legalContent.avisoLegal,
      '#privacidad': legalContent.privacidad,
      '#cookies': legalContent.cookies
    };
    setModalContent(contentMap[type]);
    setModalOpen(true);
  };

  return (
    <>
      <footer className="bg-primary text-white py-12">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {/* Brand */}
            <div>
              <h3 className="text-xl font-semibold mb-4">{siteConfig.name}</h3>
              <p className="text-white/70 text-sm mb-4">
                Cuidado de perros con cariño y tranquilidad en Pineda de Mar y alrededores.
              </p>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-semibold mb-4">Contacto</h4>
              <div className="space-y-3">
                <a
                  href={`tel:${siteConfig.phone.replace(/\s/g, '')}`}
                  className="flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm"
                  onClick={() => trackPhoneClick('footer')}
                  data-testid="phone-btn-footer"
                >
                  <Phone className="w-4 h-4" />
                  {siteConfig.phone}
                </a>
                <a
                  href={`mailto:${siteConfig.email}`}
                  className="flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm"
                >
                  <Mail className="w-4 h-4" />
                  {siteConfig.email}
                </a>
                <div className="flex items-center gap-2 text-white/70 text-sm">
                  <MapPin className="w-4 h-4" />
                  {siteConfig.zone}
                </div>
              </div>
            </div>

            {/* Legal */}
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <div className="space-y-2">
                {legalLinks.map((link) => (
                  <button
                    key={link.title}
                    onClick={() => openLegalModal(link.href)}
                    className="block text-white/70 hover:text-white transition-colors text-sm text-left"
                  >
                    {link.title}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-white/20 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-white/50 text-sm">
                © {new Date().getFullYear()} {siteConfig.name}. Todos los derechos reservados.
              </p>
              <p className="text-white/50 text-sm flex items-center gap-1">
                Hecho con <Heart className="w-4 h-4 text-red-400 fill-red-400" /> para los peludos
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Legal Modal */}
      <LegalModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        content={modalContent}
      />
    </>
  );
};

export default Footer;
